import RankingWidget, {
  RankingColoredCell,
  RankingWidgetContainerHeight,
} from "@widgets/standard/RankingWidget";
import {
  DateFilterOption,
  PageLayoutWidgetDto,
  ServiceRequestClient,
  ServiceRequestIssueRankingsWidgetDto,
  ServiceRequestSubIssueRankingDto,
} from "@lib/ShiOneClient";
import { useApi } from "../../utils";
import { useQuery } from "@tanstack/react-query";
import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import {
  useWidgetFilterUserConfig,
  NoContent,
} from "../../dashboards/framework";
import { GridRenderCellParams } from "@mui/x-data-grid-pro";
import theme from "../../../../theme";
import { DateFilterOptionHeaderAction } from "@widgets/utils/support/DateFilterOptionHeaderAction";
import { getDateRangeForFilter } from "@features/service-requests-feature/utils/serviceRequestFunctions";
import { ServiceRequestsPageTabs } from "@features/service-requests-feature/components/ServiceRequestsPageTabs";
import { SkeletonWrapper } from "shared-ui";
import { AppendIfPrefixExists } from "../../../../modules/sitePaths";
import { DateTime } from "luxon";
import { NavLink } from "react-router-dom";

const filterKey = "issue-rankings-widget-date-filter";
const IssueRankingLabelLinkStyles: CSSProperties = {
  display: "inline-flex",
};

export default function IssueRankingsWidget({
  pageLayoutWidget,
}: {
  pageLayoutWidget: PageLayoutWidgetDto;
}) {
  const [gridData, setGridData] = useState<any>([]);
  const [rawData, setRawData] = useState<{
    [key: string]: ServiceRequestSubIssueRankingDto;
  }>({});
  const [totalRecords, setTotalRecords] = useState(0);
  const { currentFilterValue, setFilter, isLoadingFilterUserConfig } =
    useWidgetFilterUserConfig(
      pageLayoutWidget.widgetId!,
      filterKey,
      DateFilterOption.CurrentMonth
    );

  let responseFunc = useGetIssueRankingsWidgetsData(currentFilterValue);
  let loading = responseFunc.status === "loading";

  useEffect(() => {
    setRawData(
      responseFunc.data?.issueRankings ??
        ({} as { [key: string]: ServiceRequestSubIssueRankingDto })
    );
    setTotalRecords(responseFunc.data?.totalRecordsCount ?? 0);
  }, [responseFunc.data]);

  useEffect(() => {
    setGridData(prepareData(rawData));
  }, [rawData]);

  const groupingColDef = {
    sortable: true,
    headerName: "Issue",
    minWidth: 180,
    flex: 0.3,
    hideable: false,
    disableReorder: true,
    valueGetter: (value: string, row: any) => {
      const url = buildIssueFilterUrl(row.issue, row.hierarchy.length > 1, currentFilterValue);
      return (
        <SkeletonWrapper loading={loading}>
          <NavLink to={url} style={IssueRankingLabelLinkStyles}>{row.issue}</NavLink>
        </SkeletonWrapper>
      );
    },
    renderHeader: (params: any) => {
      return (
        <div style={{ marginLeft: "45px" }}>{params.colDef.headerName}</div>
      );
    },
  };

  const columnDefinitions = [
    {
      id: "requests",
      headerName: "Requests",
      sortable: true,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => {
        return gridData ? (
          <RankingColoredCell
            value={params.value}
            row={params.row}
            count={Object.keys(rawData).length}
            totalRecords={totalRecords}
            baseColor={theme.palette.red}
          />
        ) : (
          <></>
        );
      },
    },
  ];

  return (
    <RankingWidget
      pageLayoutWidget={pageLayoutWidget}
      headerAction={
        <DateFilterOptionHeaderAction
          filterBy={currentFilterValue}
          setFilterBy={setFilter}
          loading={isLoadingFilterUserConfig}
          filterKey={filterKey}
        />
      }
      data={gridData}
      gridDefinitions={{ columnDefinitions, groupingColDef }}
      treeData={true}
      rowExpandLookup={React.useRef({})}
      noDataElement={<NoDataElement />}
      loading={loading}
    />
  );
}

function useGetIssueRankingsWidgetsData(currentFilterValue: DateFilterOption) {
  const api = useApi(ServiceRequestClient);

  const { dateFrom, dateTo } = useMemo(() => {
    const dateRange = getDateRangeForFilter(currentFilterValue);
    return {
      dateFrom: toNativeDate(dateRange.startDate),
      dateTo: toNativeDate(dateRange.endDate),
    };
  }, [currentFilterValue]);

  return useQuery<ServiceRequestIssueRankingsWidgetDto>(
    [
      "issueRankingsWidget",
      dateFrom?.toISOString() ?? null,
      dateTo?.toISOString() ?? null,
    ],
    () => api.issueRankingsWidget(dateFrom, dateTo)
  );
}

type WidgetDateValue = Date | DateTime | undefined;

export function toNativeDate(value: WidgetDateValue): Date | undefined {
  if (!value) {
    return undefined;
  }

  return DateTime.isDateTime(value) ? value.toJSDate() : value;
}

function NoDataElement() {
  return (
    <NoContent
      containerHeight={RankingWidgetContainerHeight}
      header={"No Requests"}
      body={
        "Create a new request to report an incident or submit a service request"
      }
      actionText={"Get Support"}
      actionLink={"/support-center/new-request"}
    />
  );
}

let prepareData = (rawData: {
  [key: string]: ServiceRequestSubIssueRankingDto;
}) => {
  let data: {}[] = [];
  let key = 0;
  Object.keys(rawData).forEach((k, i) => {
    data.push({
      hierarchy: [k],
      id: key++,
      issue: k,
      requests: rawData[k].totalRecords,
      parentIndex: i,
    });
    let subIssues = rawData[k]?.subIssueRankings ?? {};
    Object.keys(subIssues).forEach((z) => {
      data.push({
        hierarchy: [k, z],
        id: key++,
        issue: z,
        requests: subIssues[z],
        parentIndex: i,
      });
    });
  });
  return data;
};

function buildIssueFilterUrl(
  issue: string,
  isSubIssue: boolean,
  dateFilterOption: DateFilterOption
): string {
  const { startDate, endDate } = getDateRangeForFilter(dateFilterOption);

  const parts = [
    `createdDate:${encodeURIComponent(`after:${startDate.toFormat("yyyy-MM-dd")}`)}~${encodeURIComponent(`before:${endDate.toFormat("yyyy-MM-dd")}`)}`,
    isSubIssue ? `subIssueType:${encodeURIComponent(issue)}` : `issueType:${encodeURIComponent(issue)}`,
  ];

  const rawFilterInclude = parts.join(";");
  return `${AppendIfPrefixExists("/support-center/requests")}?filterInclude=${encodeURIComponent(rawFilterInclude)}#${ServiceRequestsPageTabs.allRequests}`;
}
